"use client";

import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import { useTheme } from "@mui/material/styles";
import { Card, Stack, FormLabel } from "@mui/material";

// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, RHFTextField } from "../../components/hook-form";
import Iconify from "components/iconify";
import customFetch from "hooks/fetchWrapper";

import { StyledSearchButton } from "./styles";

export default function RouteFilter() {
  const theme = useTheme();
  const isMd = theme.breakpoints.up("md");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [routes, setRoutes] = useState([]);

  // form
  const FilterSchema = Yup.object().shape({
    emirate: Yup.string().required("Emirates is required"),
    area: Yup.string(),
    route: Yup.string(),
  });

  const defaultValues = {
    emirate: "",
    area: "",
    route: "",
  };

  const methods = useForm({
    resolver: yupResolver(FilterSchema),
    defaultValues,
  });

  const { handleSubmit, watch, setValue } = methods;
  const selectedEmirate = watch("emirate");
  const selectedArea = watch("area");

  // Fetch areas when emirate changes
  useEffect(() => {
    if (selectedEmirate) {
      fetchAreasByEmirate(selectedEmirate);
      fetchRoutesByEmirate(selectedEmirate);

      // Reset area and route selection when emirate changes
      setValue("area", "");
      setValue("route", "");
    } else {
      setAreas([]);
      setRoutes([]);
    }
  }, [selectedEmirate, setValue]);

  // Filter routes when area changes
  useEffect(() => {
    if (selectedArea) {
      // Reset route selection when area changes
      setValue("route", "");
    }
  }, [selectedArea, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    console.log("Form submitted with:", data);
    setTimeout(() => {
      const queryParams = new URLSearchParams({
        emirate: data.emirate,
        area: data.area,
        route: data.route,
      }).toString();
      navigate(`/services/bus-routes/search?${queryParams}`);
      setLoading(false);
    }, 800);
  };

  useEffect(() => {
    fetchEmirates();
  }, []);

  const fetchEmirates = async () => {
    try {
      const response = await customFetch("emirates");
      // Handle both possible response formats (direct array or {status, data})
      const emiratesData = Array.isArray(response)
        ? response
        : response.status && Array.isArray(response.data)
        ? response.data
        : [];
      setEmirates(emiratesData);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const fetchAreasByEmirate = async (emirateId) => {
    try {
      const response = await customFetch(`areas/emirate/${emirateId}`);
      const areasData = response.status && Array.isArray(response.data) ? response.data : [];
      setAreas(areasData);
    } catch (error) {
      console.error("Error fetching areas by emirate:", error);
      setAreas([]);
    }
  };

  const fetchRoutesByEmirate = async (emirateId) => {
    try {
      const response = await customFetch(`routes/by-emirate/${emirateId}`);
      const routesData = response.status && Array.isArray(response.data) ? response.data : [];
      setRoutes(routesData);
    } catch (error) {
      console.error("Error fetching routes by emirate:", error);
      setRoutes([]);
    }
  };

  // Create options for the dropdowns
  const emirateOptions = [
    { label: "Select Emirates", value: "" },
    ...emirates.map((emirate) => ({
      label: emirate.name,
      value: emirate.eid.toString(),
    })),
  ];

  const areaOptions = [
    { label: "Select Area", value: "" },
    ...areas.map((area) => ({
      label: area.code ? `${area.code} - ${area.emirateName}` : area.emirateName,
      value: area.aid.toString(),
    })),
  ];

  const routeOptions = [
    { label: "Select Route", value: "" },
    ...routes
      .filter((route) => !selectedArea || route.aid.toString() === selectedArea)
      .map((route) => ({
        label: `${route.routename} - ${route.routeno}`,
        value: route.rid.toString(),
      })),
  ];

  return (
    <Card
      sx={{
        p: { xs: 2, md: 1 },
        pl: { xs: 2, md: 6 },
        width: "100%",
        maxWidth: 800,
        borderRadius: { xs: 2, md: 150 },
        position: "absolute",
        bottom: { xs: "-80px", sm: "-70px", md: "-50px" },
        border: `1px solid ${theme.palette.divider}`,
        mx: { xs: 1, sm: "auto" },
      }}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 3 }}
          alignItems="center"
          position="relative"
          sx={{ py: { xs: 1, md: 0 } }}
        >
          <div style={{ width: "100%" }}>
            <FormLabel sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>Emirates</FormLabel>
            <RHFTextField
              name="emirate"
              select
              options={emirateOptions}
              variant="standard"
              placeholder="Select Emirates"
              SelectProps={{
                sx: {
                  fontSize: { xs: 16, md: 20 },
                },
              }}
            />
          </div>
          <div style={{ width: "100%" }}>
            <FormLabel sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>Area</FormLabel>
            <RHFTextField
              name="area"
              select
              options={areaOptions}
              variant="standard"
              placeholder="Select Area"
              SelectProps={{
                sx: {
                  fontSize: { xs: 16, md: 20 },
                },
                disabled: !selectedEmirate,
              }}
            />
          </div>
          <div style={{ width: "100%" }}>
            <FormLabel sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>Route</FormLabel>
            <RHFTextField
              name="route"
              select
              options={routeOptions}
              variant="standard"
              placeholder="Select Route"
              SelectProps={{
                sx: {
                  fontSize: { xs: 16, md: 20 },
                },
                disabled: !selectedEmirate,
              }}
            />
          </div>

          <StyledSearchButton
            type="submit"
            color="primary"
            variant="contained"
            loading={loading}
            sx={{
              minWidth: { xs: "100%", md: "auto" },
              mt: { xs: 1, md: 0 },
            }}
          >
            <Iconify icon="iconoir:search" />
          </StyledSearchButton>
        </Stack>
      </FormProvider>
    </Card>
  );
}
